import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { getLastFocusableChild as _getLastFocusableChild } from '../../core/utils/focus-management/get-last-focusable-child.js';

/**
 * @internal
 * Provides the focus behavior for overlays located at the end of the DOM when navigating via keyboard.
 * By using the Ctrl/Cmd + F6 shortcut Focus is moved to the referenced close button.
 * If the overlay is closed via the keyboard, focus will be handled accordingly.
 * In case a container was passed and it contains focusable elements, focus will be moved to the first focusable element within that (necessary for the ToastContainer).
 * Otherwise, the focus will be moved back to the previously focused element when focused via the shortcut, or the last element in the document.
 *
 * @param closeButtonRef - the respective close button of the targeted overlay
 * @param containerRef - the container around the close button where the focus should remain if possible
 * @returns function for handling the focus and the state update function for storing whether the overlay was closed on keydown
 */
export function useKeyboardFocusHandling(
  closeButtonRef: MutableRefObject<HTMLButtonElement | null>,
  containerRef?: MutableRefObject<HTMLDivElement | null>,
): [() => void, MutableRefObject<boolean>] {
  const prevFocusedElement = useRef<HTMLElement | null>(null);
  const closedOnKeydown = useRef(false);

  // Focus the desired target with the Ctrl+F6 keyboard shortcut
  const focusCloseButton = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey && e.key === 'F6') || (e.metaKey && e.key === 'F6')) {
        if (closeButtonRef.current) {
          // prevent focusing on the search bar in Firefox (F6)
          e.preventDefault();
          // save previously focused element to return to when closed via the keyboard
          prevFocusedElement.current = document.activeElement as HTMLElement;
          closeButtonRef.current?.focus();
        }
      }
    },
    [closeButtonRef],
  );

  const handleFocusOnKeydown = () => {
    if (closedOnKeydown.current) {
      const lastFocusableInContainer = containerRef?.current
        ? _getLastFocusableChild(containerRef.current)
        : null;
      if (lastFocusableInContainer) {
        (lastFocusableInContainer as HTMLElement).focus();
      } else if (prevFocusedElement.current) {
        prevFocusedElement.current.focus();
        prevFocusedElement.current = null;
      } else {
        (_getLastFocusableChild(document.body) as HTMLElement)?.focus();
      }
      closedOnKeydown.current = false;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', focusCloseButton);
    return () => window.removeEventListener('keydown', focusCloseButton);
  }, [focusCloseButton]);

  return [handleFocusOnKeydown, closedOnKeydown];
}
